import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { BusinessFilter, RequestsView, RequestTypeRow } from "@/models/underwriting";
import { CollapsibleSection } from "./CollapsibleSection";
import { underwritingService } from "@/services/UnderwritingService";
import { useApi } from "@/hooks/useApi";
import { Spinner } from "@/components/ui/spinner";
import { businessToChannel } from "@/helpers/underwritingDashboard";

type StatKey = "approved" | "declined" | "alternative" | "moreInfo" | "pending" | "total";

const STAT_COLS: { key: StatKey; label: string; color: string }[] = [
  { key: "approved", label: "Approved", color: "text-foreground" },
  { key: "declined", label: "Declined", color: "text-foreground" },
  { key: "alternative", label: "Alternative", color: "text-foreground" },
  { key: "moreInfo", label: "More Info", color: "text-foreground" },
  { key: "pending", label: "Pending", color: "text-foreground" },
  { key: "total", label: "Total", color: "text-foreground font-semibold" },
];

function statValue(row: { [k in StatKey]?: number }, key: StatKey): number {
  return row[key] ?? 0;
}

function ViewToggle({ value, onChange }: { value: RequestsView; onChange: (v: RequestsView) => void }) {
  return (
    <div className="flex items-center rounded-full bg-muted p-0.5">
      {(["Total", "Unique"] as const).map((opt) => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          className={cn(
            "rounded-full px-3 py-1 text-xs font-medium transition-colors",
            value === opt ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
          )}
        >
          {opt === "Total" ? "Totals" : "Unique"}
        </button>
      ))}
    </div>
  );
}

interface RequestsByTypeSectionProps {
  start: string;
  end: string;
  business: BusinessFilter;
  refreshKey?: number;
  defaultOpen?: boolean;
}

export function RequestsByTypeSection({ start, end, business, refreshKey, defaultOpen }: RequestsByTypeSectionProps) {
  const [view, setView] = useState<RequestsView>("Total");

  const result = useApi(
    (signal) =>
      underwritingService.getRequestsByType(`${start}T00:00:00`, `${end}T23:59:59`, view === "Unique", signal),
    [start, end, view, refreshKey]
  );

  const channel = businessToChannel(business);

  // Apply the Business filter client-side using the per-channel breakdown when
  // present; otherwise fall back to the aggregate row.
  const rows = useMemo(() => {
    const data = result.data ?? [];
    if (!channel) return data;
    return data.map((row) => {
      const scoped = row.byChannel?.[channel];
      return scoped ? ({ ...row, ...scoped } as RequestTypeRow) : row;
    });
  }, [result.data, channel]);

  const totals = useMemo(() => {
    return STAT_COLS.reduce((acc, { key }) => {
      acc[key] = rows.reduce((sum, row) => sum + statValue(row, key), 0);
      return acc;
    }, {} as Record<StatKey, number>);
  }, [rows]);

  const controls = <ViewToggle value={view} onChange={setView} />;

  return (
    <CollapsibleSection title="Requests by Type" headerSlot={controls} defaultOpen={defaultOpen}>
      {result.loading ? (
        <div className="flex h-24 items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-border">
                <th className="px-4 py-2.5 text-left text-[12px] font-semibold text-muted-foreground w-40">
                  Type
                </th>
                {STAT_COLS.map(({ label }) => (
                  <th
                    key={label}
                    className="px-4 py-2.5 text-right text-[12px] font-semibold text-muted-foreground"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.requestType} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 text-[12px] text-foreground">{row.requestType}</td>
                  {STAT_COLS.map(({ key, color }) => (
                    <td key={key} className={cn("px-4 py-3 text-right tabular-nums", color)}>
                      {statValue(row, key)}
                    </td>
                  ))}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={STAT_COLS.length + 1} className="px-4 py-6 text-center text-[11px] text-muted-foreground">
                    No data for selected filters.
                  </td>
                </tr>
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="border-t border-border">
                  <td className="px-4 py-3 text-[12px] font-semibold text-foreground bg-slate-100">Total</td>
                  {STAT_COLS.map(({ key }) => (
                    <td key={key} className="px-4 py-3 text-right text-[12px] font-semibold tabular-nums text-foreground bg-slate-100">
                      {totals[key]}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </CollapsibleSection>
  );
}
